import { useEffect, useRef } from 'react'

// Crisp, readable 3D headline: the letters are extruded via stacked text-shadow
// layers (depth in em, so it scales with the responsive type) and the whole
// block tilts toward the cursor in perspective for a parallax 3D feel. No
// auto-playing animation — motion is purely cursor-driven, and disabled under
// prefers-reduced-motion (a gentle static tilt remains for depth).

// Build the extrusion: N stacked shadows marching down-right, fading light→dark,
// capped with a soft ambient shadow.
function extrusion(depthEm = 0.16, layers = 12): string {
  const shadows: string[] = []
  for (let i = 1; i <= layers; i++) {
    const t = i / layers
    const off = (depthEm * t).toFixed(3)
    const v = Math.round(0xb8 * (1 - t) + 0x12 * t)
    const hex = v.toString(16).padStart(2, '0')
    shadows.push(`${off}em ${off}em 0 #${hex}${hex}${hex}`)
  }
  shadows.push(`${(depthEm + 0.03).toFixed(3)}em ${(depthEm + 0.05).toFixed(3)}em ${(depthEm * 0.9).toFixed(3)}em rgba(0,0,0,0.5)`)
  return shadows.join(', ')
}

export default function ThreeDHeadline({
  lines,
  className = '',
  maxTiltDeg = 11,
}: {
  lines: string[]
  className?: string
  maxTiltDeg?: number
}) {
  const text = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const el = text.current
    if (!el) return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return

    let frame = 0
    const onMove = (e: PointerEvent) => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const x = (e.clientX / window.innerWidth - 0.5) * 2 // -1..1
        const y = (e.clientY / window.innerHeight - 0.5) * 2
        el.style.transform = `rotateX(${(-y * maxTiltDeg * 0.6).toFixed(2)}deg) rotateY(${(x * maxTiltDeg).toFixed(2)}deg)`
      })
    }
    const reset = () => {
      el.style.transform = 'rotateX(6deg) rotateY(-9deg)'
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerleave', reset)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerleave', reset)
    }
  }, [maxTiltDeg])

  return (
    <span
      className={`inline-block ${className}`}
      style={{ perspective: '900px' }}
    >
      <span
        ref={text}
        className="inline-block"
        style={{
          // Initial (and reduced-motion) tilt — deterministic so SSR/hydration match.
          transform: 'rotateX(6deg) rotateY(-9deg)',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.25s ease-out',
          textShadow: extrusion(),
          willChange: 'transform',
        }}
      >
        {lines.map((line, i) => (
          <span key={i} className="block">
            {line}
          </span>
        ))}
      </span>
    </span>
  )
}
